"use client"

import {
  useProfile,
  navigateWithThreadPreservation,
  DashboardSectionAction,
  DashboardSectionTitle,
  DashboardSectionSkeleton,
  PaneResponsiveCardGrid,
  useArtifactCanvas,
} from '@yeshuman/sdk'
import { useCallback, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useOpportunities, type Opportunity } from '../../hooks/use-opportunities'
import { buildApplicationStatusMap, useMyApplications } from '../../hooks/use-my-applications'
import { useNavigateToCandidateJobDetail } from '../../hooks/use-navigate-to-candidate-job-detail'
import { CandidateOpportunityJobCard } from './candidate-opportunity-job-card'
import { useSavedOpportunities } from '../../hooks/use-saved-opportunities'
import { openApplicationApplyCanvas } from './open-application-apply-canvas'
import { hasActiveApplicationForOpportunity } from './job-application-trailing'
import {
  FEATURED_HOME_SLOT_COUNT,
  fillThinFeaturedJobs,
  OPEN_JOBS_FILL_PAGE_SIZE,
} from './fill-thin-featured-jobs'
import { Briefcase } from 'lucide-react'

/** Featured-only grid: 4, else 2, else 1 — never 3. A filled grid keeps every card up to 4. */
function featuredJobsDashboardDisplayCount(total: number): number {
  if (total >= FEATURED_HOME_SLOT_COUNT) return FEATURED_HOME_SLOT_COUNT
  if (total === 3) return 2
  return total
}

export function CandidateDashboardFeaturedJobsSection() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const handleOpenJob = useNavigateToCandidateJobDetail()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const applyCanvasKeyRef = useRef(0)
  const { profile } = useProfile()

  const featuredFilters = useMemo(
    () => ({
      listing_type: 'featured' as const,
      placement: 'candidate_home' as const,
      page: 1,
      page_size: FEATURED_HOME_SLOT_COUNT,
    }),
    [],
  )

  const featuredQuery = useOpportunities(featuredFilters, { staleTime: 0 })
  const featuredCount = featuredQuery.opportunities.length
  const featuredSettled = !featuredQuery.isLoading && !featuredQuery.error
  const needsFill =
    featuredSettled && featuredCount > 0 && featuredCount < FEATURED_HOME_SLOT_COUNT
  const useOpenRoles = featuredSettled && featuredCount === 0

  const openJobsFilters = useMemo(
    () => ({
      listing_type: 'all' as const,
      page: 1,
      page_size: needsFill ? OPEN_JOBS_FILL_PAGE_SIZE : FEATURED_HOME_SLOT_COUNT,
    }),
    [needsFill],
  )
  const openJobsQuery = useOpportunities(openJobsFilters, {
    enabled: needsFill || useOpenRoles,
    staleTime: 0,
  })

  const showingFeatured = featuredCount > 0
  const opportunities = needsFill
    ? fillThinFeaturedJobs(featuredQuery.opportunities, openJobsQuery.opportunities)
    : showingFeatured
      ? featuredQuery.opportunities
      : openJobsQuery.opportunities
  const awaitingOpenJobs =
    (needsFill && openJobsQuery.isLoading) || (useOpenRoles && openJobsQuery.isLoading)
  const isLoading = featuredQuery.isLoading || awaitingOpenJobs
  const error = featuredQuery.error ?? (useOpenRoles ? openJobsQuery.error : null)
  const sectionTitle = showingFeatured ? 'Featured jobs' : 'Open roles'

  const { applications, refetch: refetchApplications } = useMyApplications({
    enabled: !!profile?.id,
  })
  const { savedIds, toggleSave, savingOpportunityId } = useSavedOpportunities()

  const handleSaveToggle = useCallback(
    (opportunityId: string) => {
      toggleSave(opportunityId, savedIds.has(opportunityId))
    },
    [savedIds, toggleSave],
  )

  const applicationStatusMap = useMemo(
    () => buildApplicationStatusMap(applications),
    [applications],
  )

  const displayTotal = needsFill
    ? Math.min(FEATURED_HOME_SLOT_COUNT, opportunities.length)
    : featuredJobsDashboardDisplayCount(opportunities.length)
  const displayedOpportunities = opportunities.slice(0, displayTotal)

  const handleApplyClick = useCallback(
    (opportunity: Opportunity) => {
      if (hasActiveApplicationForOpportunity(applicationStatusMap, opportunity.id)) return

      openApplicationApplyCanvas({
        opportunity,
        openCanvas,
        closeCanvas,
        applyCanvasKeyRef,
        onApplied: () => void refetchApplications(),
        keyPrefix: 'featured-apply',
      })
    },
    [applicationStatusMap, closeCanvas, openCanvas, refetchApplications],
  )

  const navigateAllJobs = () => {
    navigateWithThreadPreservation(navigate, '/candidate/jobs', searchParams)
  }

  const jobsContent =
    error ? (
      <div className="rounded-lg bg-destructive/5 px-4 py-6 text-center text-sm">
        <p className="font-medium text-destructive">Could not load jobs</p>
        <p className="mt-1 text-muted-foreground">{(error as Error).message}</p>
      </div>
    ) : opportunities.length === 0 && !isLoading ? (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-10 text-center">
        <Briefcase className="mb-3 h-10 w-10 text-muted-foreground" aria-hidden />
        <p className="text-sm font-medium">No open roles yet</p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">
          Check back soon — new jobs are added regularly.
        </p>
      </div>
    ) : (
      <PaneResponsiveCardGrid columnMode="pane-compact">
        {displayedOpportunities.map((opportunity) => (
          <CandidateOpportunityJobCard
            key={opportunity.id}
            className="h-full"
            opportunity={opportunity}
            application={applicationStatusMap[opportunity.id]}
            saved={savedIds.has(opportunity.id)}
            isSaving={savingOpportunityId === opportunity.id}
            onOpenJob={handleOpenJob}
            onSaveToggle={handleSaveToggle}
            onApply={handleApplyClick}
          />
        ))}
      </PaneResponsiveCardGrid>
    )

  if (isLoading) {
    return <DashboardSectionSkeleton cards={2} grid columnMode="pane-compact" />
  }

  return (
    <section className="space-y-4" aria-labelledby="dashboard-jobs-heading">
      <DashboardSectionTitle
        id="dashboard-jobs-heading"
        title={sectionTitle}
        action={<DashboardSectionAction onClick={navigateAllJobs}>View all jobs</DashboardSectionAction>}
      />
      <div className="space-y-4">{jobsContent}</div>
    </section>
  )
}
